import React, { useState } from 'react';
import { SparklesIcon, CheckCircleIcon } from './Icons';

const CONTACT_FORM_URL = process.env.REACT_APP_CONTACT_FORM_URL || '';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
    interest: 'orchestration'
  });

  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (!CONTACT_FORM_URL) {
      // Graceful fallback for local development or unconfigured env
      setSubmitted(true);
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          message: '',
          interest: 'orchestration'
        });
        setSubmitted(false);
      }, 4000);
      return;
    }

    setSending(true);

    const payload = {
      name: formData.name,
      email: formData.email,
      company: formData.company || 'Not provided',
      phone: formData.phone || 'Not provided',
      interest: formData.interest,
      message: formData.message
    };

    try {
      const response = await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (result.success) {
        setSubmitted(true);
        setSending(false);
        setTimeout(() => {
          setFormData({
            name: '',
            email: '',
            company: '',
            phone: '',
            message: '',
            interest: 'orchestration'
          });
          setSubmitted(false);
        }, 3500);
      } else {
        throw new Error(result.message || 'Failed to send');
      }
    } catch (error) {
      console.error('Email sending failed:', error);
      setSending(false);
      alert('Unable to send request currently. Please reach out directly to contact@agentexpert.in');
    }
  };

  return (
    <section id="contact" className="py-20 md:py-28 relative">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="max-w-3xl mx-auto text-center mb-16 space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-brand-500/10 text-brand-600 dark:text-brand-400 border border-brand-500/20 font-inter">
            <SparklesIcon className="w-3.5 h-3.5" />
            <span>Architecture Briefing</span>
          </div>
          <h2 className="text-3xl sm:text-5xl font-extrabold font-outfit text-slate-900 dark:text-white tracking-tight">
            Schedule an Orchestrator Demo
          </h2>
          <p className="text-slate-600 dark:text-slate-400 text-base sm:text-lg font-light leading-relaxed">
            Connect with our systems team to walk through multi-agent DAG execution, RAG indexing, and SaaS tool onboarding.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 max-w-5xl mx-auto items-start">
          {/* Left Context Column */}
          <div className="lg:col-span-5 space-y-6 text-left">
            <div className="glass-panel p-6 sm:p-7 rounded-3xl space-y-5 specular-border">
              <h3 className="text-xl font-bold text-slate-900 dark:text-white font-outfit">
                What to Expect
              </h3>
              <ul className="space-y-3.5 text-xs text-slate-600 dark:text-slate-300 font-light">
                <li className="flex items-start gap-3">
                  <CheckCircleIcon className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                  <span><strong>Live DAG Simulator:</strong> Watch multi-agent coordination across Planner, Tool, and Critic nodes in real-time.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircleIcon className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                  <span><strong>Postman & API Onboarding:</strong> Learn how to connect internal microservices without writing custom agent code.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircleIcon className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                  <span><strong>Zero-Trust Architecture:</strong> Review ThreadLocal tenant isolation and cryptographic verification.</span>
                </li>
              </ul>
            </div>

            <div className="glass-panel p-6 rounded-3xl space-y-3 text-left">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Direct Engineering Channels
              </span>
              <div className="text-xs font-mono text-slate-600 dark:text-slate-300 space-y-2">
                <div className="flex items-center gap-2">
                  <span>✉️</span>
                  <a href="mailto:contact@agentexpert.in" className="hover:text-brand-500 text-slate-800 dark:text-slate-200">
                    contact@agentexpert.in
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <span>🏢</span>
                  <span>Enterprise Support &bull; Pune / Global</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Form Column */}
          <div className="lg:col-span-7">
            <div className="glass-panel p-7 sm:p-8 rounded-3xl shadow-xl specular-border text-left">
              {submitted ? (
                <div className="text-center py-16 space-y-3">
                  <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 flex items-center justify-center mx-auto">
                    <CheckCircleIcon className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white font-outfit">
                    Demo Request Received
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 font-light max-w-sm mx-auto">
                    Our platform architecture team will contact you within 1 business day with your dedicated sandbox credentials.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Ada Lovelace"
                        className={`w-full px-3.5 py-2.5 rounded-xl text-xs bg-slate-50 dark:bg-slate-900 border ${
                          errors.name ? 'border-rose-500' : 'border-slate-200 dark:border-slate-800'
                        } text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-brand-500`}
                      />
                      {errors.name && <p className="text-[10px] text-rose-500">{errors.name}</p>}
                    </div>

                    <div className="space-y-1">
                      <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
                        Work Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="ada@company.com"
                        className={`w-full px-3.5 py-2.5 rounded-xl text-xs bg-slate-50 dark:bg-slate-900 border ${
                          errors.email ? 'border-rose-500' : 'border-slate-200 dark:border-slate-800'
                        } text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-brand-500`}
                      />
                      {errors.email && <p className="text-[10px] text-rose-500">{errors.email}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
                        Company Name
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Acme Enterprise"
                        className="w-full px-3.5 py-2.5 rounded-xl text-xs bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-brand-500"
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
                        Primary Interest Area
                      </label>
                      <select
                        name="interest"
                        value={formData.interest}
                        onChange={handleChange}
                        className="w-full px-3.5 py-2.5 rounded-xl text-xs bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-brand-500"
                      >
                        <option value="orchestration">Multi-Agent DAG Orchestrator</option>
                        <option value="rag">Hybrid Elasticsearch RAG</option>
                        <option value="tools">SaaS Tool Registry & Postman Import</option>
                        <option value="voice">Voice Bot Studio & Telephony</option>
                        <option value="enterprise">Full Platform Deployment</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
                      Workflow Objective or Requirements *
                    </label>
                    <textarea
                      rows={3}
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about the workflows or transactions you want your agents to automate..."
                      className={`w-full px-3.5 py-2.5 rounded-xl text-xs bg-slate-50 dark:bg-slate-900 border ${
                        errors.message ? 'border-rose-500' : 'border-slate-200 dark:border-slate-800'
                      } text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-brand-500`}
                    />
                    {errors.message && <p className="text-[10px] text-rose-500">{errors.message}</p>}
                  </div>

                  <button
                    type="submit"
                    disabled={sending}
                    className="w-full py-3 rounded-xl font-semibold text-xs text-white bg-gradient-to-r from-brand-600 to-indigo-600 hover:from-brand-500 hover:to-indigo-500 shadow-md shadow-brand-500/20 active:scale-[0.98] transition-all disabled:opacity-50"
                  >
                    {sending ? 'Scheduling Demo...' : 'Request Architecture Session'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
